import React from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Search,
  Settings,
  Database,
  FileText,
  Activity,
  Menu,
  X,
  Sparkles,
  LogOut,
  User
} from 'lucide-react'
import { useState } from 'react'
import DogLogo from './DogLogo'

const navigation = [
  { name: 'Admin Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'File Comparison', href: '/search', icon: Search },
]

function Sidebar({ isOpen, onClose, onLogout }) {
  const navigate = useNavigate()

  const handleLogout = () => {
    onLogout()
    navigate('/login')
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-dark-900/60 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-white/90 backdrop-blur-xl border-r border-white/50
          transform transition-all duration-300 ease-out
          lg:translate-x-0 lg:static lg:z-auto
          shadow-xl lg:shadow-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Gradient overlay for sidebar */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/30 via-transparent to-accent-purple/5 pointer-events-none" />

        <div className="relative flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-100/80">
            <div className="flex items-center group">
              <div className="relative">
                <div className="rounded-xl bg-gradient-to-br from-primary-50 to-accent-purple/20 flex items-center justify-center p-1">
                  <DogLogo size={40} />
                </div>
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary-500 to-accent-purple blur-lg opacity-20 group-hover:opacity-40 transition-opacity duration-300" />
              </div>
              <div className="ml-3">
                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-purple bg-clip-text text-transparent">
                  Cogna File Search
                </span>
                <div className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-accent-cyan" />
                  <span className="text-[10px] text-gray-400 font-medium tracking-wider uppercase">AI Powered</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100/80 transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item, index) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `nav-item group ${isActive ? 'active' : ''}`
                }
                onClick={onClose}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className={`nav-item-icon-wrapper p-2 rounded-lg transition-all duration-300 group-hover:scale-110`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <span className="ml-2 font-medium">{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* Footer with Logout */}
          <div className="p-4 border-t border-gray-100/80 bg-gradient-to-t from-gray-50/50 to-transparent">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-4 p-2 rounded-lg bg-gray-50/50">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-purple flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">Cognavia User</p>
                <p className="text-xs text-gray-400 truncate">cognavia890@ai</p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-all duration-200 group"
            >
              <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform" />
              <span>Sign Out</span>
            </button>

            {/* Status */}
            <div className="flex items-center text-sm mt-4">
              <div className="pulse-dot pulse-dot-success mr-2">
                <span></span>
              </div>
              <span className="text-gray-500">System Status: </span>
              <span className="ml-1 text-success-600 font-semibold">Online</span>
            </div>
            <p className="mt-3 text-xs text-gray-400 flex items-center">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary-500 to-accent-purple mr-2" />
              Die Drawing Similarity Matching v1.0
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}

function Header({ onMenuClick, onLogout }) {
  const navigate = useNavigate()

  const handleLogout = () => {
    onLogout()
    navigate('/login')
  }

  return (
    <header className="sticky top-0 z-30 flex items-center h-16 px-6 bg-white/80 backdrop-blur-xl border-b border-gray-100/80 shadow-sm">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 mr-4 rounded-xl hover:bg-gray-100/80 transition-all duration-200 hover:scale-105"
      >
        <Menu className="w-5 h-5 text-gray-600" />
      </button>

      <div className="flex-1">
        <h1 className="text-lg font-bold lg:hidden">
          <span className="bg-gradient-to-r from-primary-600 to-accent-purple bg-clip-text text-transparent">
            Cogna File Search
          </span>
        </h1>
      </div>

      <div className="flex items-center space-x-3">
        {/* AI Status indicator */}
        <div className="hidden sm:flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-primary-50 to-accent-purple/10 border border-primary-100/50">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary-500 to-accent-purple animate-pulse" />
          <span className="ml-2 text-xs font-medium text-primary-600">AI Ready</span>
        </div>

        <button className="p-2.5 rounded-xl hover:bg-gray-100/80 transition-all duration-200 hover:scale-105 group">
          <Settings className="w-5 h-5 text-gray-500 group-hover:text-primary-600 transition-colors group-hover:rotate-45 duration-300" />
        </button>

        {/* Logout button in header for quick access */}
        <button
          onClick={handleLogout}
          className="p-2.5 rounded-xl hover:bg-danger-50 transition-all duration-200 hover:scale-105 group"
          title="Sign Out"
        >
          <LogOut className="w-5 h-5 text-gray-500 group-hover:text-danger-600 transition-colors" />
        </button>
      </div>
    </header>
  )
}

export default function Layout({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-primary-50/30">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-purple/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-cyan/3 rounded-full blur-3xl" />
      </div>

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={onLogout} />

      <div className="relative flex-1 flex flex-col overflow-hidden">
        <Header onMenuClick={() => setSidebarOpen(true)} onLogout={onLogout} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium">Cogna File Search v1.0</span>
              <span className="text-gray-300">|</span>
              <span>A Cognavia.ai Product</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>© 2025 Cognavia.ai</span>
              <span className="text-gray-300">•</span>
              <a href="https://cognavia.ai" target="_blank" rel="noopener noreferrer" className="hover:text-primary-600 transition-colors">
                cognavia.ai
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
